import { useCurrencyFormatter } from '@mint-lib/formatting-hooks';
import { useTranslation } from '@mint-lib/i18n';
import { Activity, Calendar, Money } from '@mint-lib/icons';
import { Chip, Divider, typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { useActivitySummaryApi } from '../../../../../api/queries/useActivitySummaryApi.js';
import { BaseSummarySkeleton } from './BaseSkeleton.jsx';
import LevelDateRange from './LevelDateRange.jsx';

export const ActivitySummary = () => {
  const { t } = useTranslation('@myn/planning_v2');

  const { data: activitySummary, isPending } = useActivitySummaryApi();

  const currencyFormatter = useCurrencyFormatter();

  const formatBudget = (budget: string) => {
    return currencyFormatter.format(Number(budget), {
      currency: activitySummary?.currency ?? 'USD',
    })?.formatted;
  };

  if (isPending || !activitySummary?.dateFrom || !activitySummary?.dateTo) {
    return <BaseSummarySkeleton />;
  }

  const bodyShort01 = typographyVariants({ variant: 'bodyShort01' });
  const bodyShort02 = typographyVariants({ variant: 'bodyShort02' });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1">
        <Chip variant="outline" color="silver">
          <Activity />
          <span>{t('Activity')}</span>
        </Chip>
        <Chip variant="outline" color="silver">
          <Calendar />
          <LevelDateRange
            dateFrom={activitySummary.dateFrom}
            dateTo={activitySummary.dateTo}
          />
        </Chip>
      </div>
      <h1 className={typographyVariants({ variant: 'h3' })}>
        {activitySummary?.name}
      </h1>
      <div className="flex items-center gap-2">
        <Money />
        <span className={cn(bodyShort02, 'mr-1')}>{t('Planned budget')}</span>
        <Divider orientation="vertical" className="mx-1 self-stretch" />
        <div className="flex">
          <span className={cn(bodyShort01, 'mr-1')}>{t('Media plan:')}</span>
          <span className={bodyShort02}>
            {formatBudget(activitySummary.plannedMediaPlanBudget)}
          </span>
        </div>
        <div className="ml-4 flex">
          <span className={cn(bodyShort01, 'mr-1')}>{t('Activity:')}</span>
          <span className={bodyShort02}>
            {formatBudget(activitySummary.plannedBudget)}
          </span>
        </div>
      </div>
    </div>
  );
};
